import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { api, ApiError, errorMessage } from '@/lib/api';
import { toast, toastError } from '@/lib/toast';
import { DateInput } from '@/ui/DateTime';
import { Icon } from '@/ui/Icon';
import type { RecordSheet } from '@shared/record';
import { entityOf, keys, patchSheetCache } from './queries';

// Inline editing for the record's field rows: click or press Enter on a row, type, then Enter or click away to save
// and Esc to put it back. Saves show straight away and roll back with the server's message if it refuses them.

/** The server's message for one field, or its general message. */
export function fieldError(err: unknown, field: string): string {
  if (err instanceof ApiError) {
    const exact = err.fields[field];
    if (exact) return exact;
    const nested = Object.entries(err.fields).find(([k]) => k.startsWith(`${field}.`) || k.startsWith(`${field}[`));
    if (nested) return nested[1];
    return err.message;
  }
  return errorMessage(err);
}

type SheetPatch = Record<string, unknown>;

/** PATCHes the person or company, updating the open sheet first and rolling it back on failure. */
export function useRecordPatch(sheet: RecordSheet) {
  const qc = useQueryClient();
  const { type, id } = sheet;
  return useCallback(
    async (body: Record<string, unknown>, optimistic?: SheetPatch) => {
      const rollback = optimistic ? patchSheetCache(qc, type, id, (s) => ({ ...s, ...optimistic }) as RecordSheet) : () => undefined;
      try {
        await api.patch(`/${entityOf(type)}/${id}`, body);
      } catch (err) {
        rollback();
        throw err;
      } finally {
        void qc.invalidateQueries({ queryKey: keys.record(type, id) });
      }
    },
    [qc, type, id],
  );
}

/** Saves a change and offers to undo it from the toast. */
export function useCommit(sheet: RecordSheet) {
  const patch = useRecordPatch(sheet);
  return useCallback(
    async (label: string, change: { body: Record<string, unknown>; show?: SheetPatch }, undo: { body: Record<string, unknown>; show?: SheetPatch }) => {
      await patch(change.body, change.show);
      toast(`${label} updated`, {
        icon: 'check-circle',
        undo: () => {
          patch(undo.body, undo.show).catch((err) => toastError(err));
        },
      });
    },
    [patch],
  );
}

/** Briefly highlights a row after a save. */
export function useFlash(): [boolean, () => void] {
  const [on, setOn] = useState(false);
  const timer = useRef(0);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const fire = useCallback(() => {
    setOn(false);
    requestAnimationFrame(() => {
      setOn(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setOn(false), 1000);
    });
  }, []);
  return [on, fire];
}

/** True when a click landed on a link or button inside the row, which keeps its own meaning. */
export function fromControl(e: MouseEvent): boolean {
  const t = e.target as HTMLElement;
  const hit = t.closest('a, button, input, select, textarea');
  return !!hit && hit !== e.currentTarget;
}

export interface EditRowProps {
  label: string;
  /** The current value as text for the input. */
  value: string;
  /** How the value shows when not editing; defaults to the text. */
  display?: ReactNode;
  /** Shown muted when there is no value. */
  empty: string;
  kind?: 'text' | 'number' | 'url' | 'tel' | 'email' | 'date' | 'long';
  /** Returns a message to stop a save before it goes to the server. */
  validate?: (next: string) => string | null;
  /** Saves the new text; throws the server's error to show under the input. */
  save: (next: string) => Promise<void>;
  /** The server field name, to pick its message out of a validation error. */
  field: string;
  extra?: ReactNode;
  sub?: ReactNode;
  readOnly?: boolean;
  label2?: ReactNode;
  className?: string;
}

export function EditRow({ label, value, display, empty, kind = 'text', validate, save, field, extra, sub, readOnly, label2, className }: EditRowProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [flashing, flash] = useFlash();
  const rowRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const done = useRef(false);
  const errId = useId();
  const valueId = useId();

  const start = () => {
    if (readOnly) return;
    setDraft(value);
    setError(null);
    done.current = false;
    setEditing(true);
  };

  useEffect(() => {
    if (!editing) return;
    const el = inputRef.current;
    el?.focus();
    el?.select();
  }, [editing]);

  const finish = async (keep: boolean, refocus: boolean) => {
    if (done.current || busy) return;
    const next = draft.trim();
    if (!keep || next === value.trim()) {
      done.current = true;
      setEditing(false);
      setError(null);
      if (refocus) requestAnimationFrame(() => rowRef.current?.focus());
      return;
    }
    const problem = validate?.(next) ?? null;
    if (problem) {
      setError(problem);
      inputRef.current?.focus();
      return;
    }
    setBusy(true);
    try {
      await save(next);
      done.current = true;
      setEditing(false);
      setError(null);
      flash();
      if (refocus) requestAnimationFrame(() => rowRef.current?.focus());
    } catch (err) {
      setError(fieldError(err, field));
      requestAnimationFrame(() => inputRef.current?.focus());
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      void finish(false, true);
    } else if (e.key === 'Enter' && (kind !== 'long' || e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void finish(true, true);
    }
  };

  const cls = ['frow', readOnly ? '' : 'ed', editing ? 'editing' : '', flashing ? 'flash' : '', className].filter(Boolean).join(' ');
  if (editing) {
    const common = {
      ref: inputRef,
      value: draft,
      'aria-label': label,
      'aria-invalid': error ? true : undefined,
      'aria-describedby': error ? errId : undefined,
      disabled: busy,
      onKeyDown: onKey,
      onBlur: () => void finish(true, false),
    };
    return (
      <div className={cls} ref={rowRef}>
        <span className="fk">{label}</span>
        <span className="fv">
          {kind === 'long' ? (
            <textarea {...common} className="cell-input cell-area" rows={4} onChange={(e) => setDraft(e.target.value)} />
          ) : kind === 'date' ? (
            <DateInput {...common} className="cell-input" clearable onChange={setDraft} />
          ) : (
            <input
              {...common}
              className="cell-input"
              type={kind === 'number' ? 'text' : kind}
              inputMode={kind === 'number' ? 'numeric' : kind === 'tel' ? 'tel' : undefined}
              onChange={(e) => setDraft(e.target.value)}
            />
          )}
          {error ? (
            <span className="field-err" id={errId} role="alert">
              <Icon name="warn" className="i-xs" />
              {error}
            </span>
          ) : kind === 'long' ? (
            <span className="sub">Ctrl or ⌘ and Enter to save, Esc to cancel</span>
          ) : null}
        </span>
      </div>
    );
  }
  const shown = value.trim() ? (display ?? value) : <span className="fv-empty">{empty}</span>;
  if (readOnly)
    return (
      <div className={cls} ref={rowRef}>
        <span className="fk">{label}</span>
        <span className="fv">
          {value.trim() ? (display ?? value) : <span className="fv-empty">None</span>}
          {extra}
          {sub}
        </span>
      </div>
    );
  return (
    <div
      className={cls}
      ref={rowRef}
      role="button"
      tabIndex={0}
      aria-label={`Edit ${label}`}
      aria-describedby={valueId}
      onClick={(e) => {
        if (!fromControl(e)) start();
      }}
      onKeyDown={(e) => {
        if (e.target === e.currentTarget && e.key === 'Enter') {
          e.preventDefault();
          start();
        }
      }}
    >
      <span className="fk">
        {label}
        {label2}
      </span>
      <span className="fv" id={valueId}>
        {shown}
        {extra}
        {sub}
      </span>
    </div>
  );
}
